import React, { useEffect } from "react"

import * as Sentry from "@sentry/react"

import { Spinner } from "@/components/ui"
import { API_ORIGIN } from "@/lib/constants"
import { useCurrentUserQuery } from "@/src/graphql/public-generated-rq"

interface AuthContextData {
  isAuthenticated?: boolean
  isAdmin?: boolean
  pid?: string | null
  email?: string | null
  name?: string | null
}

const AuthContext = React.createContext<AuthContextData | undefined>(undefined)

/*
 * Component that queries for authentication status on public pages.
 */
export function PublicAuthProvider({ children }: { children: React.ReactNode }) {
  const { data } = useCurrentUserQuery()
  return <AuthProvider data={data?.currentUser}>{children}</AuthProvider>
}

/*
 * Component that queries for authentication status on admin pages.
 */
export function AdminAuthProvider({ children }: { children: React.ReactNode }) {
  const { data, isLoading } = useCurrentUserQuery()
  if (isLoading) return <Spinner />
  if (!data?.currentUser.isAdmin) return null
  return <AuthProvider data={data?.currentUser}>{children}</AuthProvider>
}

function AuthProvider({
  data,
  children,
}: {
  data?: AuthContextData
  children: React.ReactNode
}) {
  const isAuthenticated = data?.isAuthenticated
  const isAdmin = data?.isAdmin
  const pid = data?.pid
  const email = data?.email
  const name = data?.name

  useEffect(() => {
    if (data) {
      Sentry.setUser({
        id: data.pid ?? undefined,
        email: data.email ?? undefined,
        username: data?.name ?? undefined,
      })
    }
  }, [data])

  return (
    <AuthContext.Provider value={{ isAuthenticated, isAdmin, pid, email, name }}>
      {children}
    </AuthContext.Provider>
  )
}

/*
 * Hook that provides access to authenticated user information.
 */
export function useAuth(): AuthContextData {
  const context = React.useContext(AuthContext)
  if (context === undefined) {
    console.warn("useAuth should be used within an AuthProvider")
    return {
      isAdmin: false,
      isAuthenticated: false,
    }
  }
  return context
}

/*
 * Optimistically render children in the hopes that we are already authenticated.
 * Hide them and redirect to /login if we do not have an authenticated user.
 *
 * This provides better performance in the common case at the expense of a flash of
 * partial content before redirecting if the user is not authenticated.
 */
export function AuthRequired({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuth()

  useEffect(() => {
    if (isAuthenticated === false) {
      const next = window.location
      location.href =
        API_ORIGIN +
        "/login?next=" +
        encodeURIComponent(next.pathname + next.search + next.hash)
    }
  }, [isAuthenticated])

  // We optimistically load the children before finding out if we are actually
  // authenticated for better performance.
  if (isAuthenticated === false) {
    return null
  } else {
    return <>{children}</>
  }
}
